/**
 * <neroli-marquee> — scrolling banner.
 *
 * The scroll itself is CSS. This handles the three things CSS cannot:
 *
 * 1. Duration from measured width, so "speed" means a constant number of
 *    pixels per second. Setting a loop time instead makes the banner faster
 *    every time the merchant removes a block, which is the opposite of what
 *    they expect.
 * 2. Cloning until the track fills the viewport. Two copies is enough for a
 *    long banner and leaves a visible gap on a short one.
 * 3. The pause control, and starting stopped when the OS asks for reduced
 *    motion.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class NeroliMarquee extends HTMLElement {
  connectedCallback() {
    // Selector-based rather than class-based, so any section can reuse this
    // engine with its own class names instead of borrowing another section's
    // stylesheet.
    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.tracks = Array.from(this.querySelectorAll('[data-marquee-track]'));
    this.toggle = this.querySelector('[data-marquee-toggle]');
    if (!this.viewport || !this.tracks.length) return;

    if (this.dataset.direction === 'right') this.classList.add('marquee--reverse');

    this.fill();
    this.measure();

    // Content width changes with the viewport, and a stale duration means the
    // speed drifts after a rotation or a resize.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this);
    }

    if (this.toggle) {
      this.toggle.addEventListener('click', this.onToggle);
    }

    // Someone who asked for no motion gets none, rather than a slower version
    // of the same thing. The control still starts it if they want it.
    if (REDUCED_MOTION.matches) {
      this.setRunning(false);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
  }

  /**
   * Two tracks cover a banner wider than the screen. A short one — three
   * words on a desktop monitor — needs more copies or the loop shows a gap.
   */
  fill() {
    const original = this.tracks[0];
    if (!original) return;

    const needed = this.offsetWidth * 2;
    let guard = 0;

    // The guard is a stop, not a target: an empty track would otherwise loop
    // forever adding zero width.
    while (this.viewport.scrollWidth < needed && guard < 8) {
      const clone = original.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // Clones must never be reachable: a duplicated link is an extra tab
      // stop that leads to the same place and moves while focused.
      clone.querySelectorAll('a, button').forEach((el) => {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      });
      this.viewport.appendChild(clone);
      this.tracks.push(clone);
      guard += 1;
    }
  }

  measure() {
    const track = this.tracks[0];
    if (!track) return;

    const width = track.scrollWidth;
    if (!width) return;

    const speed = Number(this.dataset.speed) || 60;
    const duration = width / speed;

    this.style.setProperty('--mq-duration', `${duration}s`);
  }

  onToggle = () => {
    const running = this.toggle.getAttribute('aria-pressed') === 'true';
    this.setRunning(!running);
  };

  setRunning(running) {
    this.style.setProperty('--mq-state', running ? 'running' : 'paused');

    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(running));

    const pauseIcon = this.toggle.querySelector('.marquee__toggle-icon--pause');
    const playIcon = this.toggle.querySelector('.marquee__toggle-icon--play');
    const label = this.toggle.querySelector('[data-marquee-toggle-label]');

    if (pauseIcon) pauseIcon.hidden = !running;
    if (playIcon) playIcon.hidden = running;
    if (label) {
      label.textContent = running
        ? this.toggle.dataset.labelPause
        : this.toggle.dataset.labelPlay;
    }
  }
}

if (!customElements.get('neroli-marquee')) {
  customElements.define('neroli-marquee', NeroliMarquee);
}
